import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/web.dom-collections.for-each.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { createContext, useRef } from 'react';
import { useItemList } from 'use-item-list';
import { KEY } from 'utils/ui/constants';
import useEventListener from 'hooks/useEventListener';
import useOnClickOutside from 'hooks/useOnClickOutside';
import usePopover from 'hooks/usePopover';
import usePopoverState from 'hooks/usePopoverState';
import useUniqueId from 'hooks/useUniqueId';
var MenuContext = /*#__PURE__*/createContext(null);
export function useMenuContext() {
  var context = React.useContext(MenuContext);
  if (context === null) {
    throw new Error("useMenuContext must be used within a Menu component's context");
  }
  return context;
}
function Menu(_ref) {
  var idProp = _ref.id,
    _ref$menuPlacement = _ref.menuPlacement,
    menuPlacement = _ref$menuPlacement === void 0 ? 'bottom' : _ref$menuPlacement,
    menuPositionFixed = _ref.menuPositionFixed,
    children = _ref.children;
  var buttonRef = useRef(null);
  var menuListRef = useRef(null);
  var popoverRef = useRef(null);
  var id = useUniqueId(idProp);
  var popover = usePopover({
    ref: popoverRef,
    referenceRef: buttonRef,
    placement: menuPlacement,
    positionFixed: menuPositionFixed,
    adaptivePositioning: true
  });
  var itemList = useItemList({
    id: id,
    // Needs to be defined later to get access to popover state
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    onSelect: onSelect
  });
  var _usePopoverState = usePopoverState({
      onOpen: function onOpen() {
        var _menuListRef$current;
        (_menuListRef$current = menuListRef.current) === null || _menuListRef$current === void 0 ? void 0 : _menuListRef$current.focus();
        itemList.setHighlightedItem(0);
      },
      onClose: function onClose() {
        var _popoverRef$current;
        // If focus is on or within the popover when it's closed,
        // we move it back to the button.
        if (popoverRef.current === document.activeElement || (_popoverRef$current = popoverRef.current) !== null && _popoverRef$current !== void 0 && _popoverRef$current.contains(document.activeElement)) {
          var _buttonRef$current;
          (_buttonRef$current = buttonRef.current) === null || _buttonRef$current === void 0 ? void 0 : _buttonRef$current.focus();
        }
      }
    }),
    isOpen = _usePopoverState.isOpen,
    open = _usePopoverState.open,
    close = _usePopoverState.close,
    toggle = _usePopoverState.toggle;
  function onSelect() {
    itemList.clearHighlightedItem();
    close();
  }
  function handleGlobalMenuKeyEvents(event) {
    var _popoverRef$current2;
    if (event.key === KEY.ESC || event.key === KEY.TAB) {
      event.preventDefault();
      close();
    }
    var isFocusInMenuOrOnButton = buttonRef.current === document.activeElement || popoverRef.current === document.activeElement || ((_popoverRef$current2 = popoverRef.current) === null || _popoverRef$current2 === void 0 ? void 0 : _popoverRef$current2.contains(document.activeElement));
    if (isFocusInMenuOrOnButton) {
      if (event.key === KEY.ARROW_UP) {
        event.preventDefault();
        itemList.moveHighlightedItem(-1);
      }
      if (event.key === KEY.ARROW_DOWN) {
        event.preventDefault();
        itemList.moveHighlightedItem(1);
      }
      if (event.key === KEY.SPACE || event.key === KEY.ENTER) {
        var item = itemList.getHighlightedItem();
        if (item) {
          var _item$ref$current;
          event.preventDefault();
          // Trigger a click on the highlighted item
          // to select it (unless it's disabled)
          if (!((_item$ref$current = item.ref.current) !== null && _item$ref$current !== void 0 && _item$ref$current.ariaDisabled)) {
            var _item$ref$current2;
            (_item$ref$current2 = item.ref.current) === null || _item$ref$current2 === void 0 ? void 0 : _item$ref$current2.click();
          }
        }
      }
      itemList.highlightItemByString(event);
    }
  }
  function handleButtonKeyEvents(event) {
    if (event.key === KEY.ARROW_DOWN) {
      event.preventDefault();
      open();
    }
  }

  // Handle global keyboard events when the menu is open
  useEventListener('keydown', handleGlobalMenuKeyEvents, {
    isEnabled: isOpen
  });

  // Close the menu when clicking outside of it
  useOnClickOutside(popoverRef, close, isOpen);
  var highlightedItemId = itemList.useHighlightedItemId();
  var menuListProps = {
    ref: menuListRef,
    role: 'menu',
    tabIndex: -1,
    id: itemList.listId,
    'aria-activedescendant': isOpen ? highlightedItemId : null,
    'aria-labelledby': itemList.controllerId
  };
  var menuButtonProps = {
    ref: popover.setReferenceRef,
    id: itemList.controllerId,
    'aria-haspopup': true,
    'aria-controls': itemList.listId,
    'aria-expanded': isOpen,
    onKeyDown: handleButtonKeyEvents,
    onClick: toggle
  };
  return /*#__PURE__*/React.createElement(MenuContext.Provider, {
    value: {
      popover: _objectSpread(_objectSpread({}, popover), {}, {
        isOpen: isOpen,
        toggle: toggle,
        open: open,
        close: close
      }),
      menuListProps: menuListProps,
      menuButtonProps: menuButtonProps,
      itemList: itemList
    }
  }, children);
}
export { MenuContext };
export default Menu;